import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { btn } from "@/lib/ui";

const EMPTY = { name: "", phone: "", message: "", website: "" };
const IDLE = { state: "idle", message: "" };

const isMobile = (phone) => /^[6-9]\d{9}$/.test(phone.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, ""));

export function validateCallback(form) {
  const errs = {};
  if (form.name.trim().length < 2) errs.name = "Please enter your name";
  if (!isMobile(form.phone)) errs.phone = "Enter a valid 10-digit mobile number";
  return errs;
}

function useCallbackRequest() {
  const [status, setStatus] = useState(IDLE);
  const send = async (form) => {
    setStatus({ state: "sending", message: "" });
    try {
      await api.post("/enquiries", form);
      setStatus({ state: "done", message: "" });
      return true;
    } catch (err) {
      setStatus({ state: "error", message: apiError(err) });
      return false;
    }
  };
  return { status, send, reset: () => setStatus(IDLE) };
}

const CallbackSuccess = ({ onReset }) => (
  <div className="rounded-3xl bg-brand-greenTint p-6 text-brand-greenDeep" role="status" data-testid="callback-success">
    <CheckCircle2 className="h-7 w-7" aria-hidden="true" />
    <p className="mt-3 text-lg font-bold">Thank you — we'll call you back soon.</p>
    <p className="mt-1 text-base">Our team usually returns calls during clinic hours.</p>
    <button type="button" onClick={onReset} className="mt-4 text-sm font-semibold underline" data-testid="callback-another-btn">Send another request</button>
  </div>
);

const FieldError = ({ id, text }) => (text ? <p id={id} className="field-error">{text}</p> : null);

export const CallbackForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const { status, send, reset } = useCallbackRequest();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const errs = validateCallback(form);
    setErrors(errs);
    if (!Object.keys(errs).length && (await send(form))) setForm(EMPTY);
  };

  if (status.state === "done") return <CallbackSuccess onReset={reset} />;
  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="callback-form">
      <div>
        <label htmlFor="cb-name" className="field-label">Name</label>
        <input id="cb-name" className="field-input" autoComplete="name" value={form.name} onChange={set("name")} aria-invalid={!!errors.name} aria-describedby={errors.name ? "cb-name-err" : undefined} data-testid="callback-name-input" />
        <FieldError id="cb-name-err" text={errors.name} />
      </div>
      <div>
        <label htmlFor="cb-phone" className="field-label">Mobile number</label>
        <input id="cb-phone" className="field-input" type="tel" inputMode="numeric" autoComplete="tel-national" maxLength={14} value={form.phone} onChange={set("phone")} aria-invalid={!!errors.phone} aria-describedby={errors.phone ? "cb-phone-err" : undefined} data-testid="callback-phone-input" />
        <FieldError id="cb-phone-err" text={errors.phone} />
      </div>
      <div>
        <label htmlFor="cb-msg" className="field-label">Message <span className="font-normal text-slate-600">(optional)</span></label>
        <textarea id="cb-msg" rows={3} maxLength={1000} className="field-input h-auto py-3" value={form.message} onChange={set("message")} data-testid="callback-message-input" />
      </div>
      <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      {status.state === "error" && <p className="field-error" role="alert" data-testid="callback-error">{status.message}</p>}
      <button type="submit" disabled={status.state === "sending"} className={`${btn("blue", "md")} w-full sm:w-auto`} data-testid="callback-submit-btn">
        {status.state === "sending" ? "Sending…" : "Request a callback"}
      </button>
    </form>
  );
};
